import { useCallback, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';
import { Button as GddsButton } from '@geberit/gdds';

// components
import { Translation } from 'components/Translation/Translation';
import { BUTTON_COLORS, BUTTON_TYPES, Button } from 'components/Button/Button';
import { Icon } from 'components/Icon/Icon';

// utils
import { currentCatalogSelector } from 'utils/selectors/productCatalogSelectors';
import getEndpoint from 'utils/endpoints';
import { useXy } from 'utils/hooks/use-xy';
import { useTranslationFunction } from 'utils/hooks/use-translations';

type ProductDetailInfoToolsProps = {
  productKey: string;
  scrollToDetails: (stateName, id) => void;
  sparePartsGiven: boolean;
  documentsGiven: boolean;
};

function ProductDetailInfoTools({
  productKey,
  scrollToDetails,
  sparePartsGiven,
  documentsGiven,
}: Readonly<ProductDetailInfoToolsProps>) {
  const link = useRef(null);
  const isXy = useXy();
  const catalog = useSelector(currentCatalogSelector);
  const state = useStore();
  const translate = useTranslationFunction();

  const DetailButton = isXy ? GddsButton : Button;

  const getProductpdf = useCallback(
    async (e) => {
      e.preventDefault();
      fetch(`${getEndpoint('productpdf', state)}/products/${productKey}/pdf?lang=${catalog}`).then(
        async (response) => {
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.setAttribute('download', `${productKey}.pdf`);
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(url);
          }
        },
      );
    },
    [state, productKey, catalog],
  );

  return (
    <div className="c-product-detail__info-tools">
      <div>
        {isXy ? (
          <GddsButton
            stylingType="flatHighlight"
            onClick={getProductpdf}
            download={`${productKey}.pdf`}
            size="standard"
            symbol="pdf"
            aria-label={translate('arialabel_productdata_downloadpdf')}
          >
            <Translation id="web20_product_articles_download_datasheet" />
          </GddsButton>
        ) : (
          <a
            href="#"
            onClick={getProductpdf}
            className="c-product-detail__download-link link-button link-button--transparent"
            ref={link}
            download={`${productKey}.pdf`}
            aria-label={translate('arialabel_productdata_downloadpdf')}
          >
            <Icon symbol="file" />
            <Translation id="web20_product_articles_download_datasheet" />
          </a>
        )}
      </div>
      {sparePartsGiven && (
        <div>
          <DetailButton
            symbol={isXy ? 'settings' : 'options'}
            onClick={() => scrollToDetails('spareParts', 'product-spareParts')}
            {...(isXy
              ? { stylingType: 'flatHighlight' }
              : { type: BUTTON_TYPES.SEAMLESS, color: BUTTON_COLORS.PRIMARY })}
          >
            <Translation id="web20_product_articles_scroll_to_spare_parts" />
          </DetailButton>
        </div>
      )}
      {documentsGiven && (
        <div>
          <DetailButton
            symbol={isXy ? 'document' : 'search-document'}
            onClick={() => scrollToDetails('documents', 'product-documents')}
            {...(isXy
              ? { stylingType: 'flatHighlight' }
              : { type: BUTTON_TYPES.SEAMLESS, color: BUTTON_COLORS.PRIMARY })}
          >
            <Translation id="web20_product_articles_scroll_to_documents" />
          </DetailButton>
        </div>
      )}
    </div>
  );
}

export default ProductDetailInfoTools;
